import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { loadPersonalFile } from "./patientPersonalFileSlice";
import { Modal, Table, Typography, Button, Space } from "antd";
import Surface from "../../components/layouts/Surface";
import AddPatientPersonalFile from "./components/AddPatientPersonalFile";
import Date from "../../components/tables/Date";
import FormHeaderWrapper from "../../components/forms/FormHeader.style";
import { getFullName } from "../../components/util/helpers";
import {
  exportReportToPdf,
  getPdfMargins,
} from "../../components/util/helpers";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { loadRecord } from "../patientDetails/patientDetailsSlice";
const { Title, Text } = Typography;

const PatientPersonalFile = (props) => {
  const dispatch = useDispatch();
  let location = useLocation();
  const [visible, setVisible] = useState(false);
  const [addCommentVisible, setAddCommentVisible] = useState(false);
  const [mode, setMode] = useState(null);
  const [activeRecord, setActiveRecord] = useState(null);
  const items = useSelector((state) => state.patientPersonalFile.personalFile);
  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );
  const loading = useSelector(
    (state) => state.pendingActions["patientPersonalFile/load"]
  );
  const intl = useIntl();

  useEffect(() => {
    if (activePatient.id) {
      dispatch(loadPersonalFile(activePatient.id));
    }
  }, [dispatch, activePatient.id]);

  const onAdd = (record) => {
    setMode("new");
    setAddCommentVisible(true);
    setVisible(true);
  };

  const getItems = (data) => {
    let final = [];
    data.forEach((item, i) => {
      const temp = {
        created_at: moment(item.created_at).format("DD/MM/YYYY"),
        created_by: item.created_by ? item.created_by.name : "",
        executed_at: moment(item.executed_at).format("DD/MM/YYYY"),
        reported_by: item.reported_by
          ? getFullName(item.reported_by.first_name, item.reported_by.surename)
          : "",
        comment: item.comment.replace(/\r\n/g, ""),
        title: item.title,
      };
      final.push(temp);
    });
    return final;
  };

  const setHeader = (doc, record) => {
    doc.setFontSize(16);
    doc.setTextColor(40);
    doc.setR2L(true);
    doc.setFont("Rubik", "normal"); // set font
    var title = "תיק אישי";
    const margins = getPdfMargins(doc);
    var hLoc = doc.internal.pageSize.getWidth() - margins * 2;
    var subTitle = `${activePatient.first_name} ${activePatient.surename}`;
    const report = "תאריך הדפסה";
    const date = `${moment().format("HH:mm DD/MM/YYYY")}`;
    const reportDate = `${report} - ${date}`;
    const idLabel = "תעודת זהות";
    const reportId = activePatient.local_id_number
      ? `${idLabel} - ${activePatient.local_id_number}`
      : ``;
    doc.text(title, hLoc, 20, {
      align: "right",
    });
    doc.setFontSize(12);
    doc.text(subTitle, hLoc, 26, {
      align: "right",
    });
    doc.text(reportDate, margins, 20, {
      align: "left",
    });
    doc.text(reportId, margins, 26, {
      align: "left",
    });
  };

  const onPrint = () => {
    const patientName = getFullName(
      activePatient.first_name,
      activePatient.surename
    );
    exportReportToPdf(
      patientName,
      getItems(items),
      setHeader,
      null,
      null,
      null,
      items,
      "paging",
      40,
      intl,
      "personal_file",
      {
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 60 },
          5: { cellWidth: 25 },
        },
        margin: { top: 36 },
      },
      null,
      "print"
    );
  };

  const hide = () => {
    setVisible(false);
    dispatch(loadPersonalFile(activePatient.id));
    setAddCommentVisible(false);
    setActiveRecord(null);
  };

  const getPatient = (id) => {
    const isAdmin = location.pathname.includes("admin");
    const params = {
      id: id,
      branchId: activePatient.branch,
      admin: isAdmin,
    };
    dispatch(loadRecord(params));
  };

  return (
    <Surface>
      <FormHeaderWrapper>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            <FormattedMessage id={`app.documentation.title`} />
          </Title>
          <Space>
            <Button type="primary" onClick={onAdd}>
              <FormattedMessage id={`app.personal_file.addDocumentation`} />
            </Button>
            {items && items.length > 0 && (
              <Button type="text" onClick={onPrint}>
                <FormattedMessage id={`app.personal_file.printDocumentation`} />
              </Button>
            )}
          </Space>
        </div>
      </FormHeaderWrapper>

      <Table
        bordered
        rowKey={(record) => record.id}
        scroll={{ y: window.innerHeight - 526 }}
        dataSource={items}
        pagination={false}
        columns={[
          {
            title: intl.formatMessage({
              id: "app.personal_file.title",
            }),
            dataIndex: "title",
            width: "160px",
          },
          {
            title: intl.formatMessage({
              id: "app.personal_file.comment",
            }),
            dataIndex: "comment",
          },
          {
            title: intl.formatMessage({
              id: "app.personal_file.reported_by",
            }),
            width: "160px",
            dataIndex: "reported_by",
            render(text, record) {
              return (
                <Text>
                  {text ? getFullName(text.surename, text.first_name) : null}
                </Text>
              );
            },
          },
          {
            title: intl.formatMessage({
              id: "app.personal_file.executed_at",
            }),
            dataIndex: "executed_at",
            width: "120px",
            type: "date",
            required: true,
            render(text, record) {
              return <Date text={text} format={"L"} />;
            },
          },
          {
            title: intl.formatMessage({
              id: "app.personal_file.created_by",
            }),
            width: "160px",
            dataIndex: "created_by",
            render(text, record) {
              return <Text>{text.name}</Text>;
            },
          },
          {
            title: intl.formatMessage({
              id: "app.personal_file.created_at",
            }),
            dataIndex: "created_at",
            width: "120px",
            type: "date",
            required: true,
            render(text, record) {
              return <Date text={text} format={"L"} />;
            },
          },
        ]}
        loading={loading}
      />
      <Modal
        closable={false}
        maskClosable={false}
        open={visible}
        footer={null}
        onCancel={hide}
        title={null}
        width={"fit-content"}
        styles={{
          body: {
            padding: 0,
          },
        }}
        destroyOnClose={true}
      >
        {addCommentVisible ? (
          <AddPatientPersonalFile
            activePatient={activePatient}
            hide={hide}
            mode={mode}
            record={activeRecord}
            getPatient={getPatient}
            addText={
              mode === "edit" ? (
                <FormattedMessage id="app.personal_file.editDocumentation" />
              ) : (
                <FormattedMessage id="app.personal_file.addNewDocumentation" />
              )
            }
          />
        ) : null}
      </Modal>
    </Surface>
  );
};
export default PatientPersonalFile;
